import { compareModelsByFamily } from '../utils/modelFamily';
import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Button, Select, Tooltip, Tag, Alert, Input, Checkbox } from 'antd';
import {
  DownOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  isLanguageBeta,
  hasQuotationOptions,
  getQuotationStyle,
  getDefaultQuotationStyle,
  getQuotationTooltip,
} from '../LanguageConfig';
import ModalInlineNotice from '../components/ModalInlineNotice';
import RecommendedTag from '../components/RecommendedTag'

type DemoBetaFeedbackIssueType = 'quality' | 'style' | 'formatting' | 'terminology' | 'other';

type DemoTranslationModalProps = {
  visible: boolean;
  onClose: () => void;
  bookLanguage?: string;
  totalWords?: number;
  scope?: 'chapter' | 'book';
  chapterOrder?: number;
  translatedChaptersByLang?: Record<string, number[]>;
  chapters?: { order: number; word_count?: number }[];
  languages?: string[];
  bookguid?: string;
  onTranslate?: (params: {
    version: string;
    targetLanguage: string;
    quotationStyle?: string;
    aiModel?: string;
  }) => void;
};

const DemoTranslationModal: React.FC<DemoTranslationModalProps> = ({
  visible,
  onClose,
  bookLanguage = 'English',
  totalWords = 0,
  scope = 'book',
  chapterOrder,
  translatedChaptersByLang,
  chapters,
  languages,
  bookguid,
  onTranslate,
}) => {
  // Demo-only local state
  const [translateVersion, setTranslateVersion] = useState<'Original' | 'Proofread'>('Original');
  const [targetLanguage, setTargetLanguage] = useState<string>('');
  const [selectedAIModel, setSelectedAIModel] = useState<string>('');
  const [quotationStyle, setQuotationStyle] = useState<string>('');
  const [betaLanguageWarning, setBetaLanguageWarning] = useState<boolean>(false);
  const [isBetaFeedbackModalVisible, setIsBetaFeedbackModalVisible] = useState<boolean>(false);
  const [betaFeedbackIssueType, setBetaFeedbackIssueType] = useState<DemoBetaFeedbackIssueType>('quality');
  const [betaFeedbackText, setBetaFeedbackText] = useState<string>('');
  const [betaFeedbackContactEmail, setBetaFeedbackContactEmail] = useState<string>('');
  const [betaFeedbackIncludeContext, setBetaFeedbackIncludeContext] = useState<boolean>(true);
  const [betaFeedbackSubmitting, setBetaFeedbackSubmitting] = useState<boolean>(false);
  const [betaFeedbackError, setBetaFeedbackError] = useState<string | null>(null);

  // Tier types and helpers
  type Tier = 'Good' | 'Better' | 'Best';

  type DemoModel = {
    id: string;
    label: string;
    tags?: string[];
    defaultTier?: Tier;
    languageTierOverrides?: Record<string, Tier>;
  };

  const getEffectiveTier = (model: DemoModel, lang?: string): Tier | undefined => {
    if (lang && model.languageTierOverrides && model.languageTierOverrides[lang]) {
      return model.languageTierOverrides[lang];
    }
    return model.defaultTier;
  };

  // Hardcoded non-testing AI models for demo (no online lookups)
  const HARD_MODELS: DemoModel[] = [
    { id: 'Chat-GPT', label: 'Chat-GPT', tags: ['Legacy'] },
    {
      id: 'ChatGPT4.1',
      label: 'ChatGPT 4.1',
      defaultTier: 'Better',
      languageTierOverrides: {
        Ukrainian: 'Good',
        Polish: 'Good',
        'Brazilian Portuguese': 'Best',
      },
    },
    {
      id: 'ChatGPT5',
      label: 'ChatGPT 5',
      defaultTier: 'Best',
    },
    { id: 'Claude', label: 'Claude 3.5', tags: ['Legacy'] },
    { id: 'Claude3', label: 'Claude 3', tags: ['Legacy'] },
    {
      id: 'Claude3.7',
      label: 'Claude 3.7',
      defaultTier: 'Better',
      languageTierOverrides: {
        German: 'Best',
        'Brazilian Portuguese': 'Best',
      },
    },
    { id: 'DeepSeek', label: 'DeepSeek', tags: ['Legacy', '50% OFF'] },
    {
      id: 'DeepSeekR10528',
      label: 'DeepSeek R1',
      tags: ['50% OFF'],
      defaultTier: 'Good',
    },
    {
      id: 'Gemini2.5Pro',
      label: 'Gemini 2.5 Pro',
      defaultTier: 'Best',
      languageTierOverrides: {
        German: 'Best',
        'Brazilian Portuguese': 'Good',
      },
    },
  ];
  
  const ALLOWED_MODEL_IDS = new Set(['ChatGPT5', 'Gemini2.5Pro', 'Claude3.7']);
  const languageOptions = useMemo(() => (languages && languages.length > 0 ? languages : ['German', 'French']), [languages]);

  const sortedModels = useMemo(() => {
    const arr = [...HARD_MODELS];
    arr.sort((a, b) => compareModelsByFamily(
      { ...a, displayName: a.label },
      { ...b, displayName: b.label }
    ));
    return arr;
  }, [targetLanguage]);
  
  const renderModelLabel = (m: DemoModel) => {
    return (
      <span>
        {m.label}
        {(m.tags || []).map((t) => {
            const isDiscount = t.toLowerCase().includes('off');
            const isLegacy = t.toLowerCase().includes('legacy');
            if (isLegacy) {
              return (
                <Tag
                  key={t}
                  style={{ backgroundColor: '#ffd6e7', color: '#9e1068', borderColor: '#9e1068', marginLeft: 8 }}
                >
                  {t}
                </Tag>
              );
            }
            return (
              <Tag key={t} color={isDiscount ? 'green' : 'blue'} style={{ marginLeft: 8 }}>
                {t}
              </Tag>
            );
          })}
      </span>
    );
  };

  // On language change, if a user-selected allowed model exists, keep it; otherwise choose best among allowed
  useEffect(() => {
    if (!targetLanguage) return;
    const current = HARD_MODELS.find((m) => m.id === selectedAIModel);
    const currentAllowed = !!current && ALLOWED_MODEL_IDS.has(current.id);
    if (currentAllowed) return;

    const findByTier = (tier: Tier) =>
      HARD_MODELS.find(
        (m) => ALLOWED_MODEL_IDS.has(m.id) && getEffectiveTier(m, targetLanguage) === tier
      );

    const desired =
      findByTier('Best') ||
      findByTier('Better') ||
      findByTier('Good') ||
      HARD_MODELS.find((m) => ALLOWED_MODEL_IDS.has(m.id));

    if (desired) {
      setSelectedAIModel(desired.id);
    }
  }, [targetLanguage, selectedAIModel]);

  // Demo credit balance and cost
  // Demo credits loaded from public JSON (mirrors DemoMetadataTranslationModal)
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [initialCreditBalance, setInitialCreditBalance] = useState<number>(150000);
  useEffect(() => {
    let isMounted = true;
    const loadCredits = async () => {
      try {
        const res = await fetch('/demo/credits.json', { cache: 'no-store' });
        if (!res.ok) throw new Error(`Failed to load credits.json: ${res.status}`);
        const data = await res.json();
        if (isMounted && typeof data?.credit_balance === 'number') {
          // Allow local override for persistence across modals
          try {
            const raw = localStorage.getItem('demoCreditBalance');
            const saved = raw ? parseInt(raw, 10) : NaN;
            const start = Number.isFinite(saved) ? saved : data.credit_balance;
            setCreditBalance(start);
            setInitialCreditBalance(data.credit_balance);
          } catch {
            setCreditBalance(data.credit_balance);
            setInitialCreditBalance(data.credit_balance);
          }
        }
      } catch {
        if (isMounted) {
          setCreditBalance(150000);
          setInitialCreditBalance(150000);
        }
      }
    };
    loadCredits();
    // Reset handler
    const onReset = () => {
      setCreditBalance((_) => initialCreditBalance);
      try { localStorage.removeItem('demoCreditBalance'); } catch {}
    };
    window.addEventListener('demo:reset', onReset as unknown as EventListener);
    return () => { isMounted = false; window.removeEventListener('demo:reset', onReset as unknown as EventListener); };
  }, []);
  const [originalChapters, setOriginalChapters] = useState<{ order: number; word_count?: number }[] | null>(null);

  // Load Original/chapters.json to compute costs using original word counts
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!bookguid) {
        setOriginalChapters(null);
        return;
      }
      try {
        const res = await fetch(`/demo/books/${bookguid}/Original/chapters.json`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data)
            ? data.map((e: any, i: number) => ({ order: typeof e.order === 'number' ? e.order : i, word_count: typeof e.word_count === 'number' ? e.word_count : 0 }))
            : [];
          if (!cancelled) setOriginalChapters(list);
        } else {
          if (!cancelled) setOriginalChapters(null);
        }
      } catch {
        if (!cancelled) setOriginalChapters(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [bookguid]);

  const computedCost = useMemo(() => {
    const list = (originalChapters && originalChapters.length > 0) ? originalChapters : (chapters || []);
    if (!targetLanguage) return totalWords || 0;
    const tr = translatedChaptersByLang || {};
    const done = new Set(tr[targetLanguage] || []);
    if (scope === 'chapter' && typeof chapterOrder === 'number') {
      const ch = list.find(c => c.order === chapterOrder);
      return (typeof ch?.word_count === 'number' ? ch!.word_count! : 0) || 0;
    }
    return list.reduce((sum, c) => sum + (done.has(c.order) ? 0 : ((typeof c.word_count === 'number' ? c.word_count : 0) || 0)), 0);
  }, [originalChapters, chapters, translatedChaptersByLang, targetLanguage, scope, chapterOrder, totalWords]);
  const discountedCost = computedCost; // no discount logic in demo
  const hasEnoughCredits = creditBalance !== null && creditBalance >= discountedCost;

  // language options are provided by parent or all languages via LanguageConfig
  const ALLOWED_LANGUAGES = new Set(['French', 'German']);

  useEffect(() => {
    if (!visible) {
      // reset when closing
      setTranslateVersion('Original');
      setTargetLanguage('');
      setSelectedAIModel('');
      setQuotationStyle('');
      setBetaLanguageWarning(false);
      setIsBetaFeedbackModalVisible(false);
      setBetaFeedbackIssueType('quality');
      setBetaFeedbackText('');
      setBetaFeedbackContactEmail('');
      setBetaFeedbackIncludeContext(true);
      setBetaFeedbackError(null);
    }
  }, [visible]);

  const closeBetaFeedbackModal = () => {
    if (betaFeedbackSubmitting) return;
    setIsBetaFeedbackModalVisible(false);
    setBetaFeedbackIssueType('quality');
    setBetaFeedbackText('');
    setBetaFeedbackContactEmail('');
    setBetaFeedbackIncludeContext(true);
    setBetaFeedbackError(null);
  };

  const handleSubmitBetaFeedback = () => {
    const trimmedFeedback = betaFeedbackText.trim();
    const trimmedEmail = betaFeedbackContactEmail.trim();
    if (trimmedFeedback.length < 10) {
      setBetaFeedbackError('Please share a bit more detail (at least 10 characters).');
      return;
    }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setBetaFeedbackError('Please enter a valid email address or leave it blank.');
      return;
    }

    setBetaFeedbackSubmitting(true);
    setBetaFeedbackError(null);
    try {
      const submission = {
        submittedAt: new Date().toISOString(),
        source: 'demo_translation_modal_beta',
        targetLanguage,
        issueType: betaFeedbackIssueType,
        feedback: trimmedFeedback,
        contactEmail: trimmedEmail || null,
        includeContext: betaFeedbackIncludeContext,
        context: betaFeedbackIncludeContext
          ? {
              scope,
              translateVersion,
              selectedAIModel,
              quotationStyle,
              computedCost,
            }
          : null,
      };
      const raw = localStorage.getItem('demoTranslationBetaFeedback');
      const existing = raw ? JSON.parse(raw) : [];
      const next = Array.isArray(existing) ? [...existing, submission] : [submission];
      localStorage.setItem('demoTranslationBetaFeedback', JSON.stringify(next.slice(-50)));
      closeBetaFeedbackModal();
    } catch {
      setBetaFeedbackError('Unable to submit feedback right now. Please try again.');
    } finally {
      setBetaFeedbackSubmitting(false);
    }
  };

  const handleTargetLanguageChange = (value: string) => {
    setTargetLanguage(value);
    setBetaLanguageWarning(isLanguageBeta(value));
    // set default quotation style if language has options
    const def = getDefaultQuotationStyle(value);
    setQuotationStyle(def || '');
  };

  const needsQuotationStyle = hasQuotationOptions(targetLanguage) && !quotationStyle;

  const statusMessages: { type: 'error' | 'warning' | 'success'; message: string; icon: React.ReactNode }[] = [];

  if (!targetLanguage) {
    statusMessages.push({
      type: 'error',
      message: 'Please select a target language.',
      icon: <ExclamationCircleOutlined />,
    });
  }
  if (targetLanguage && targetLanguage === bookLanguage) {
    statusMessages.push({
      type: 'error',
      message: 'Origin and target languages must be different.',
      icon: <ExclamationCircleOutlined />,
    });
  }
  if (needsQuotationStyle) {
    statusMessages.push({
      type: 'error',
      message: `Please select a quotation style for ${targetLanguage}.`,
      icon: <ExclamationCircleOutlined />,
    });
  }
  if (!hasEnoughCredits) {
    statusMessages.push({
      type: 'error',
      message: 'You do not have enough credits for this translation.',
      icon: <ExclamationCircleOutlined />,
    });
  }
  if (translateVersion === 'Proofread') {
    // In demo, we assume not all chapters are proofread, so warn if they try to choose Proofread
    statusMessages.push({
      type: 'warning',
      message: 'Original (Proofread) is typically unavailable in the demo.',
      icon: <WarningOutlined />,
    });
  }
  if (scope === 'book' && targetLanguage && targetLanguage !== bookLanguage) {
    statusMessages.push({
      type: 'success',
      message: 'ScribeShadow will only translate untranslated chapters.',
      icon: <CheckCircleOutlined />,
    });
  }
  if (
    scope === 'chapter' &&
    targetLanguage &&
    translatedChaptersByLang &&
    typeof chapterOrder === 'number' &&
    (translatedChaptersByLang[targetLanguage] || []).includes(chapterOrder)
  ) {
    statusMessages.push({
      type: 'warning',
      message: `This chapter is already translated in ${targetLanguage}. Re-translation will incur the same cost as translation.`,
      icon: <WarningOutlined />,
    });
  }
  if (scope === 'book' && targetLanguage && computedCost === 0) {
    statusMessages.push({
      type: 'warning',
      message: `All chapters are already translated in ${targetLanguage}.`,
      icon: <WarningOutlined />,
    });
  }

  const handleTranslate = () => {
    // Deduct credits if possible, then pass control to parent handler
    if (!targetLanguage) return;
    const cost = discountedCost;
    if (creditBalance === null || creditBalance < cost) return;
    setCreditBalance((prev) => {
      const next = typeof prev === 'number' ? prev - cost : prev;
      try { if (typeof next === 'number') localStorage.setItem('demoCreditBalance', String(next)); } catch {}
      return next;
    });
    if (onTranslate) {
      onTranslate({
        version: translateVersion,
        targetLanguage,
        quotationStyle,
        aiModel: selectedAIModel,
      });
    }
    onClose();
  };

  const disabledTranslate =
    !targetLanguage ||
    targetLanguage === bookLanguage ||
    !hasEnoughCredits ||
    needsQuotationStyle ||
    (scope === 'book' && !!targetLanguage && computedCost === 0);

  return (
    <>
    <Modal
      title={scope === 'chapter' ? 'Translate Chapter' : 'Translate Book'}
      visible={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose} data-testid="tutorial:translation:cancel">
          Cancel
        </Button>,
        <Button
          key="translate"
          type="primary"
          onClick={handleTranslate}
          disabled={disabledTranslate}
          data-testid="tutorial:translation:start"
        >
          Translate
        </Button>,
      ]}
    >
      <div data-testid="tutorial:translation:modal">
      {/* Version */}
      <div data-testid="tutorial:translation:version" style={{ marginBottom: 15 }}>
        <label style={{ display: 'block', marginBottom: 5 }}>
          <strong>Version to Translate:</strong>
        </label>
        <Select
          value={translateVersion}
          onChange={(val: 'Original' | 'Proofread') => setTranslateVersion(val)}
          style={{ width: '100%' }}
          suffixIcon={
            <span className="ant-select-suffix">
              <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
            </span>
          }
        >
          <Select.Option value="Original">Original</Select.Option>
          <Select.Option value="Proofread" disabled>
            Original (Proofread) (Unavailable in demo)
          </Select.Option>
        </Select>
      </div>

      {/* Origin language */}
      <div data-testid="tutorial:translation:origin-language" style={{ marginBottom: 15 }}>
        <label style={{ display: 'block', marginBottom: 5 }}>
          <strong>Origin Language:</strong>
        </label>
        <div style={{ padding: '4px 0' }}>{bookLanguage}</div>
      </div>

      {/* Target language */}
      <div data-testid="tutorial:translation:target-language" style={{ marginBottom: 15 }}>
        <label style={{ display: 'block', marginBottom: 5 }}>
          <strong>Target Language:</strong>
        </label>
          <Select
            value={targetLanguage}
            onChange={handleTargetLanguageChange}
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            dropdownStyle={{ maxHeight: '80vh' }}
            listHeight={500}
            suffixIcon={
              <span className="ant-select-suffix">
                <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
              </span>
            }
          >
            {languageOptions.map((lang) => (
              <Select.Option key={lang} value={lang} disabled={!ALLOWED_LANGUAGES.has(lang)}>
                <span>
                  {lang}
                  {isLanguageBeta(lang) && (
                    <Tag color="orange" style={{ marginLeft: 8, fontSize: '0.75em', padding: '0 4px', lineHeight: 1 }}>
                      BETA
                    </Tag>
                  )}
                </span>
              </Select.Option>
            ))}
          </Select>
      </div>

      {/* Beta warning */}
      {betaLanguageWarning && targetLanguage && (
        <ModalInlineNotice
          tone="warning"
          message={
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <strong>{targetLanguage} translation is currently in beta.</strong>
              <Button
                type="link"
                size="small"
                style={{ paddingInline: 0, height: 'auto', fontSize: 12 }}
                onClick={() => {
                  setBetaFeedbackError(null);
                  setIsBetaFeedbackModalVisible(true);
                }}
              >
                Give feedback
              </Button>
              </div>
            </>
          }
          style={{ marginBottom: 15 }}
        />
      )}

      {/* AI Model + Quotation style */}
      <div style={{ display: 'flex', gap: 15, marginBottom: 15 }}>
        {/* AI Model */}
        <div data-testid="tutorial:translation:model" style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: 5 }}>
            <strong>AI Model:</strong>
          </label>
          <Select
            value={targetLanguage ? (selectedAIModel || undefined) : undefined}
            onChange={(value) => setSelectedAIModel(value)}
            style={{ width: '100%' }}
            placeholder={targetLanguage ? 'Select an AI Model' : 'Choose a Language to select an AI Model'}
            disabled={!targetLanguage}
            suffixIcon={
              <span className="ant-select-suffix">
                <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
              </span>
            }
          >
            {sortedModels.map((m) => (
              <Select.Option key={m.id} value={m.id} disabled={!ALLOWED_MODEL_IDS.has(m.id)}>
                {renderModelLabel(m)}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Quotation style */}
        <div data-testid="tutorial:translation:quotation-style" style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: 5 }}>
            <strong>Quotation Style:</strong>
            <Tooltip title={getQuotationTooltip(targetLanguage)}>
              <QuestionCircleOutlined style={{ marginLeft: 5, color: '#1677ff', cursor: 'pointer' }} />
            </Tooltip>
            {needsQuotationStyle && (
              <Tag color="red" style={{ marginLeft: 8 }}>
                Required
              </Tag>
            )}
          </label>

          {(() => {
            const quotationConfig = getQuotationStyle(targetLanguage);
            if (!quotationConfig) {
              return (
                <>
                  <Select disabled value="unknown" style={{ width: '100%' }}>
                    <Select.Option value="unknown"> </Select.Option>
                  </Select>
                  <div style={{ color: '#999', fontSize: 12, marginTop: 5 }}></div>
                </>
              );
            }
            if (quotationConfig.options) {
              return (
                <Select
                  value={quotationStyle}
                  onChange={setQuotationStyle}
                  placeholder="Please select a quotation style"
                  style={{
                    width: '100%',
                    ...(needsQuotationStyle ? { borderColor: '#ff4d4f' } : {}),
                  }}
                  status={needsQuotationStyle ? 'error' : undefined}
                  suffixIcon={
                    <span className="ant-select-suffix">
                      <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
                    </span>
                  }
                >
                  {quotationConfig.options.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label} ({option.marks})
                      {option.isDefault && (
                        <RecommendedTag style={{ position: 'relative', top: '-0.5px' }} />
                      )}
                    </Select.Option>
                  ))}
                </Select>
              );
            }
            if (quotationConfig.fixed) {
              return (
                <>
                  <Select disabled value="fixed" style={{ width: '100%' }}>
                    <Select.Option value="fixed">
                      {quotationConfig.fixed.name} ({quotationConfig.fixed.marks})
                    </Select.Option>
                  </Select>
                  <div style={{ color: '#999', fontSize: 12, marginTop: 5 }}>
                    Quotation style is fixed for {targetLanguage}
                  </div>
                </>
              );
            }
          })()}
        </div>
      </div>

      {/* Credit info */}
      <div data-testid="tutorial:translation:cost-readiness" style={{ background: '#f0f7ff', padding: 15, borderRadius: 6, marginBottom: 15 }}>
        <div style={{ marginBottom: 8 }}>
          <strong>Credit Balance: </strong>
          <span>{creditBalance !== null ? creditBalance : 'Loading...'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <strong>Translation Credit Cost: </strong>
          <span style={{ marginLeft: 5, marginRight: 5 }}>{discountedCost}</span>
          <Tooltip title="Credits are charged at one per translated word, matching your document word count.">
            <QuestionCircleOutlined style={{ color: '#1677ff' }} />
          </Tooltip>
        </div>
      </div>

      {/* Alerts */}
      <div data-testid="tutorial:translation:status-messages" style={{ marginTop: 12 }}>
        {statusMessages.map((msg, idx) => (
          <Alert
            key={idx}
            className="modal-alert-compact"
            message={msg.message}
            type={msg.type}
            icon={msg.icon}
            style={{ marginBottom: 8 }}
            showIcon
            closable={false}
          />
        ))}
      </div>
      </div>
    </Modal>
    <Modal
      title="Share Translation Beta Feedback"
      visible={isBetaFeedbackModalVisible}
      onCancel={closeBetaFeedbackModal}
      onOk={handleSubmitBetaFeedback}
      okText="Submit Feedback"
      confirmLoading={betaFeedbackSubmitting}
      cancelButtonProps={{ disabled: betaFeedbackSubmitting }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div>
          <label style={{ display: 'block', marginBottom: 6 }}>
            <strong>Feedback Topic</strong>
          </label>
          <Select
            value={betaFeedbackIssueType}
            onChange={(value) => setBetaFeedbackIssueType(value as DemoBetaFeedbackIssueType)}
            style={{ width: '100%' }}
          >
            <Select.Option value="quality">Translation quality</Select.Option>
            <Select.Option value="style">Voice or style</Select.Option>
            <Select.Option value="formatting">Formatting or punctuation</Select.Option>
            <Select.Option value="terminology">Terminology or glossary</Select.Option>
            <Select.Option value="other">Other</Select.Option>
          </Select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6 }}>
            <strong>What should we improve?</strong>
          </label>
          <Input.TextArea
            value={betaFeedbackText}
            onChange={(event) => setBetaFeedbackText(event.target.value)}
            rows={5}
            maxLength={2000}
            showCount
            placeholder="Tell us what you expected and what happened."
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6 }}>
            <strong>Contact Email (optional)</strong>
          </label>
          <Input
            value={betaFeedbackContactEmail}
            onChange={(event) => setBetaFeedbackContactEmail(event.target.value)}
            placeholder="you@example.com"
          />
        </div>
        <Checkbox
          checked={betaFeedbackIncludeContext}
          onChange={(event) => setBetaFeedbackIncludeContext(Boolean(event.target.checked))}
        >
          Include current translation settings with this feedback.
        </Checkbox>
        {betaFeedbackError && (
          <Alert
            className="modal-alert-compact"
            type="error"
            showIcon={false}
            message={betaFeedbackError}
          />
        )}
      </div>
    </Modal>
    </>
  );
};

export default DemoTranslationModal;
